import { useState, useRef, useCallback } from 'react'
import { useToken } from './useToken'
import { useSSEProgress, type SSEType } from './useSSEProgress'

interface JobRunnerConfig {
  /** SSE endpoint type ('scan' or 'application') */
  sseType: SSEType
  /** Start the job and return the job ID. Must be memoized. */
  startJob: (token: string) => Promise<string>
  /** Called when SSE reports completion. Must be memoized. */
  onComplete: (token: string) => Promise<void> | void
  /** If set and returns a truthy string, that string is shown as an error instead of starting. */
  prerequisiteError?: string | null
}

interface JobRunnerResult {
  progress: number
  error: string | null
  /** Kick off the job (called by the component's mount effect) */
  start: () => Promise<void>
  /** Retry after error */
  retry: () => void
}

/**
 * Shared logic for async-job steps (ScanRunning, DraftGenerating).
 * Handles: token fetch -> start job -> SSE progress -> onComplete callback.
 */
export function useJobRunner(config: JobRunnerConfig): JobRunnerResult {
  const { getToken } = useToken()

  const [jobId, setJobId] = useState<string | null>(null)
  const [token, setToken] = useState<string | null>(null)
  const [startError, setStartError] = useState<string | null>(null)
  const startedRef = useRef(false)

  // Keep refs to avoid stale closures in SSE callback
  const onCompleteRef = useRef(config.onComplete)
  onCompleteRef.current = config.onComplete
  const getTokenRef = useRef(getToken)
  getTokenRef.current = getToken

  const handleSSEComplete = useCallback(async () => {
    const accessToken = await getTokenRef.current()
    await onCompleteRef.current(accessToken)
  }, [])

  const { progress, error: sseError, retry: retrySSE } = useSSEProgress({
    type: config.sseType,
    jobId,
    token,
    onComplete: handleSSEComplete,
  })

  const startJobRef = useRef(config.startJob)
  startJobRef.current = config.startJob

  const start = useCallback(async () => {
    if (startedRef.current) return
    startedRef.current = true
    setStartError(null)

    if (config.prerequisiteError) {
      setStartError(config.prerequisiteError)
      return
    }

    try {
      const accessToken = await getTokenRef.current()
      setToken(accessToken)
      const id = await startJobRef.current(accessToken)
      setJobId(id)
    } catch (err) {
      setStartError(
        err instanceof Error
          ? err.message
          : 'Something went wrong. Please try again.'
      )
    }
  }, [config.prerequisiteError])

  const retry = useCallback(() => {
    if (startError) {
      startedRef.current = false
      start()
    } else {
      retrySSE()
    }
  }, [startError, start, retrySSE])

  return {
    progress,
    error: startError || sseError,
    start,
    retry,
  }
}
